import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { SchemaOrg, organizationSchema } from '../../utils/seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('メッセージは500文字以内で入力してください。');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('inquiryType', formData.inquiryType);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4jul31gb9i7lkq8r6h0', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'お問い合わせ | おかやま保険株式会社',
    description: 'おかやま保険株式会社へのお問い合わせはこちらから。お電話またはフォームにてお気軽にご相談ください。',
    url: `${siteUrl}/contact`,
    mainEntity: organizationSchema,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'お問い合わせ',
        item: `${siteUrl}/contact`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <SchemaOrg type="ContactPage" data={contactPageSchema} />
      <SchemaOrg type="WebPage" data={breadcrumbSchema} />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center overflow-hidden" style={{ height: '40vh' }}>
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/e374a18d758942ae26d8a15390900da0.jpg"
              alt="お問い合わせ"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">お問い合わせ</h1>
            <p className="text-base sm:text-lg md:text-xl mb-3 md:mb-4">お気軽にご相談ください</p>
            <nav className="text-sm">
              <Link to="/" className="hover:text-[#FF9B7A] transition-colors cursor-pointer">ホーム</Link>
              <span className="mx-2">&gt;</span>
              <span>お問い合わせ</span>
            </nav>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-3xl font-bold text-[#2C3E50] mb-6 md:mb-8">
                  お問い合わせフォーム
                </h2>
                
                <form id="contactForm" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-[#2C3E50] mb-2">
                      お名前 <span className="text-[#FF9B7A]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF9B7A] text-sm"
                      placeholder="山田太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-[#2C3E50] mb-2">
                      メールアドレス <span className="text-[#FF9B7A]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF9B7A] text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-[#2C3E50] mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF9B7A] text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-bold text-[#2C3E50] mb-2">
                      お問い合わせ内容 <span className="text-[#FF9B7A]">*</span>
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF9B7A] text-sm"
                    >
                      <option value="">選択してください</option>
                      <option value="生命保険">生命保険について</option>
                      <option value="医療保険">医療保険について</option>
                      <option value="自動車保険">自動車保険について</option>
                      <option value="火災保険">火災保険について</option>
                      <option value="個人年金保険">個人年金保険について</option>
                      <option value="法人保険">法人保険について</option>
                      <option value="保険見直し">保険の見直し相談</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-[#2C3E50] mb-2">
                      メッセージ <span className="text-[#FF9B7A]">*</span>
                      <span className="text-xs text-gray-500 ml-2">（500文字以内）</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF9B7A] text-sm resize-vertical"
                      placeholder="お気軽にご相談内容をお書きください"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#FF9B7A] text-white py-4 rounded-lg font-bold hover:bg-[#FF8A65] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 text-sm">
                        お問い合わせありがとうございます。2営業日以内にご返信いたします。
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 text-sm">
                        送信に失敗しました。お手数ですがもう一度お試しください。
                      </p>
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-3xl font-bold text-[#2C3E50] mb-6 md:mb-8">
                  お電話でのお問い合わせ
                </h2>
                
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm mb-6 md:mb-8">
                  <div className="text-center mb-6 md:mb-8">
                    <div className="inline-block bg-[#FF9B7A] text-white px-5 md:px-6 py-2 md:py-3 rounded-full text-base md:text-lg font-bold mb-4">
                      無料相談受付中
                    </div>
                    <a
                      href="tel:0860123456"
                      className="block text-3xl md:text-4xl font-black text-[#2C3E50] hover:text-[#FF9B7A] transition-colors cursor-pointer"
                    >
                      086-012-3456
                    </a>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-time-line text-xl text-[#FF9B7A]"></i>
                      <div>
                        <p className="font-bold text-[#2C3E50] text-sm md:text-base">営業時間</p>
                        <p className="text-gray-600 text-sm md:text-base">9:00〜18:00（日曜・祝日除く）</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <i className="ri-map-pin-line text-xl text-[#FF9B7A]"></i>
                      <div>
                        <p className="font-bold text-[#2C3E50] text-sm md:text-base">所在地</p>
                        <p className="text-gray-600 text-sm md:text-base">岡山市北区○○町1-2-3</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <i className="ri-car-line text-xl text-[#FF9B7A]"></i>
                      <div>
                        <p className="font-bold text-[#2C3E50] text-sm md:text-base">出張エリア</p>
                        <p className="text-gray-600 text-sm md:text-base">岡山市・倉敷市・総社市</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#FF9B7A] rounded-2xl p-6 md:p-8 text-white">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">
                    🏠 出張相談も承ります
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed mb-4 md:mb-6">
                    小さなお子様がいらっしゃるご家庭や、お仕事でお忙しい方のために、
                    ご自宅やお近くのカフェでの出張相談も行っております。
                  </p>
                  <div className="space-y-2 md:space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-lg"></i>
                      <span>出張費無料</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-lg"></i>
                      <span>土日対応可能</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-lg"></i>
                      <span>何度でも無料相談</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}